import {isSupplementaryFormComplete} from '@sollapay/domain/rules';
import {DEFAULT_LINK_EXPIRY_DURATION} from '@sollapay/enums';
import {parseMoney} from '@sollapay/utils';
import {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {config} from '@/app/app.config';
import {
  useAddBuyersToPurchaseForm,
  useBuyerSubTabNavigation,
  useCreatePaymentInstructionMutation,
  usePaymentInitiationMethod,
  usePaymentTransferType,
  usePaymentsQuery,
  usePurchaseBuyers
} from '@/features/buyers/hooks';
import {
  computeRemainingAmountNis,
  formatPurchaseAssetNames,
  hasSupplementaryInput,
  isBuyerFlowNextDisabled,
  isPaymentNextDisabled,
  isPaymentRequestDirty,
  toBuyerOption
} from '@/features/buyers/utils';
import {useTrustTypeStrategy} from '@/features/trust-accounts/providers';

import {BuyersModal} from './BuyersModal';
import {AddBuyerToPurchaseTabContent} from './BuyersModal/add-buyer-to-purchase/AddBuyerToPurchaseTabContent';
import {CreateBuyerStepsNav} from './BuyersModal/create-buyer/CreateBuyerStepsNav';
import {PaymentRequestTabContent} from './BuyersModal/payment-request/PaymentRequestTabContent';
import {BuyerSuccessPromptModal} from './BuyerSuccessPromptModal';
import {PaymentSentModal} from './PaymentSentModal';

import type {SubTab} from './BuyersModal/create-buyer/CreateBuyerTabContent';
import type {LinkExpiryDuration} from '@sollapay/enums';
import type {BuyerDTO, FatcaCrsSupplementaryPayload} from '@sollapay/types';
import type {FC, ReactNode} from 'react';

type FlowStep = 'idle' | 'add-buyer' | 'success-prompt' | 'payment-request' | 'payment-sent';

export interface AddBuyerToPurchaseFlowProps {
  trustAccountId: string;
  /** The existing purchase to add buyer(s) to — property/price details are never re-entered. */
  purchaseId: string;
  trigger: (onOpen: () => void) => ReactNode;
}

/**
 * Sibling of `AddBuyerFlow` for a trust type whose purchase already exists — same success-prompt →
 * payment-request → payment-sent chain, but step 1 collects buyer details only (no asset/purchase-
 * price/document upload, since the purchase already has both).
 */
export const AddBuyerToPurchaseFlow: FC<AddBuyerToPurchaseFlowProps> = ({
  trustAccountId,
  purchaseId,
  trigger
}) => {
  const {t} = useTranslation('trustAccounts');
  const strategy = useTrustTypeStrategy();
  const [flowStep, setFlowStep] = useState<FlowStep>('idle');
  const [createdBuyer, setCreatedBuyer] = useState<BuyerDTO | null>(null);
  const [expiryDuration, setExpiryDuration] = useState<LinkExpiryDuration>(
    DEFAULT_LINK_EXPIRY_DURATION
  );
  const [idempotencyKey, setIdempotencyKey] = useState<string>('');
  const [customAmountNis, setCustomAmountNis] = useState('');
  const [selectedBuyerId, setSelectedBuyerId] = useState('');
  const [supplementaryValues, setSupplementaryValues] =
    useState<FatcaCrsSupplementaryPayload | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('buyer-details');

  const {buyers: purchaseBuyers} = usePurchaseBuyers(trustAccountId, purchaseId, createdBuyer?.id);
  // Skipped until the payment-request step is reached — the disabled query's list would be empty.
  const {data: payments = []} = usePaymentsQuery(
    flowStep === 'payment-request' ? trustAccountId : ''
  );
  const paymentBuyerOptions =
    purchaseBuyers.length > 0
      ? purchaseBuyers.map(toBuyerOption)
      : createdBuyer
        ? [toBuyerOption(createdBuyer)]
        : [];
  const effectiveBuyerId = selectedBuyerId || (createdBuyer?.id ?? '');
  const selectedBuyerForPayment =
    purchaseBuyers.find(b => b.id === effectiveBuyerId) ?? createdBuyer;

  const {
    form,
    buyerFields,
    appendBuyer,
    removeBuyer,
    replaceBuyers,
    handleSave,
    handleCancel,
    saveError,
    isSaving,
    ai
  } = useAddBuyersToPurchaseForm({
    trustAccountId,
    purchaseId,
    supplementaryValues,
    onSuccess: (buyers: BuyerDTO[]) => {
      const buyer = buyers[0];
      if (!buyer) return;
      setCreatedBuyer(buyer);
      setFlowStep('success-prompt');
    }
  });

  const createPaymentMutation = useCreatePaymentInstructionMutation(trustAccountId);

  const purchase = createdBuyer?.purchases[0];
  const maxAmountNis = purchase
    ? parseMoney(purchase.purchasePriceNis).percentOf(strategy.maxCollectionPercentage).toString()
    : '0';

  // The purchase is pre-existing — other buyers may already have payment instructions on it.
  const remainingAmountNis = useMemo(
    () => (purchase ? computeRemainingAmountNis(purchase, payments, maxAmountNis) : maxAmountNis),
    [purchase, payments, maxAmountNis]
  );

  const amount = config.features.allowCustomPaymentAmount ? customAmountNis : maxAmountNis;
  const {transferType, isZahavOnly, setTransferType, resetTransferType} =
    usePaymentTransferType(amount);
  const {initiationMethod, selectedInitiationMethod, setInitiationMethod, resetInitiationMethod} =
    usePaymentInitiationMethod(selectedBuyerForPayment?.partyType);

  const resetFlow = useCallback(() => {
    setFlowStep('idle');
    setCreatedBuyer(null);
    resetInitiationMethod();
    resetTransferType();
    setExpiryDuration(DEFAULT_LINK_EXPIRY_DURATION);
    setIdempotencyKey('');
    setCustomAmountNis('');
    setSelectedBuyerId('');
    setSupplementaryValues(null);
    setActiveSubTab('buyer-details');
    handleCancel();
  }, [handleCancel, resetTransferType, resetInitiationMethod]);

  const handleOpenAddBuyer = useCallback(() => {
    setFlowStep('add-buyer');
  }, []);

  const handleContinueToPayment = () => {
    setIdempotencyKey(crypto.randomUUID());
    setSelectedBuyerId(createdBuyer?.id ?? '');
    setFlowStep('payment-request');
  };

  const handleSendPaymentRequest = async () => {
    if (!purchase || !initiationMethod || !idempotencyKey) return;

    try {
      await createPaymentMutation.mutateAsync({
        trustBuyerPurchaseId: purchase.id,
        buyerPartyId: effectiveBuyerId,
        amount,
        initiationMethod,
        transferType,
        idempotencyKey,
        expiryDuration
      });
      setFlowStep('payment-sent');
    } catch {
      // Error surfaced via createPaymentMutation.error → saveError prop
      // Regenerate key so a retry doesn't reuse a potentially consumed key
      setIdempotencyKey(crypto.randomUUID());
    }
  };

  const isBuyerFormValid = form.formState.isValid && buyerFields.length > 0;

  const {handleContinue, handleSubTabChange} = useBuyerSubTabNavigation(form, setActiveSubTab);

  const isSupplementarySigned =
    supplementaryValues !== null &&
    isSupplementaryFormComplete(supplementaryValues.declarationValues);

  const isNextDisabled = isPaymentNextDisabled({
    initiationMethod,
    allowCustomAmount: config.features.allowCustomPaymentAmount,
    customAmountNis,
    remainingAmountNis,
    enforcesCollectionCap: strategy.enforcesCollectionCap
  });

  return (
    <>
      {trigger(handleOpenAddBuyer)}

      {/* Screen 1 — Add Buyer Modal */}
      <BuyersModal
        isOpen={flowStep === 'add-buyer'}
        isFlushBody={activeSubTab === 'buyer-details'}
        onOpenChange={open => {
          if (!open && flowStep === 'add-buyer') resetFlow();
        }}
        isDirty={form.formState.isDirty || hasSupplementaryInput(supplementaryValues)}
        discardDescription={t('view.buyers.addBuyerModal.discardDescription')}
        backClosesModal={activeSubTab === 'buyer-details'}
        activeTab="create-buyer"
        isBuyerCreated={false}
        nextLabel={
          activeSubTab === 'buyer-details'
            ? t('view.buyers.addBuyerModal.footer.continue')
            : t('view.buyers.addBuyerModal.footer.addBuyer')
        }
        nextDisabled={isBuyerFlowNextDisabled({
          isDetailsStep: activeSubTab === 'buyer-details',
          buyerCount: buyerFields.length,
          isSupplementarySigned
        })}
        nextIsLoading={isSaving}
        backLabel={t('view.buyers.addBuyerModal.footer.back')}
        onNextClick={activeSubTab === 'buyer-details' ? handleContinue : handleSave}
        onBackClick={() => setActiveSubTab('buyer-details')}
        subHeader={
          <CreateBuyerStepsNav
            activeSubTab={activeSubTab}
            onSubTabChange={handleSubTabChange}
            isBuyerFormValid={isBuyerFormValid}
          />
        }
      >
        <AddBuyerToPurchaseTabContent
          trustAccountId={trustAccountId}
          form={form}
          buyerFields={buyerFields}
          appendBuyer={appendBuyer}
          removeBuyer={removeBuyer}
          replaceBuyers={replaceBuyers}
          saveError={saveError}
          onSupplementaryValuesChange={setSupplementaryValues}
          activeSubTab={activeSubTab}
          ai={ai}
        />
      </BuyersModal>

      {/* Screen 2 — Success Prompt */}
      <BuyerSuccessPromptModal
        isOpen={flowStep === 'success-prompt'}
        buyerName={createdBuyer?.fullName ?? ''}
        onContinueToPayment={handleContinueToPayment}
        onBackToBuyers={resetFlow}
      />

      {/* Screen 3 — Payment Request Modal */}
      <BuyersModal
        isOpen={flowStep === 'payment-request'}
        onOpenChange={open => {
          if (!open && flowStep === 'payment-request') resetFlow();
        }}
        isDirty={isPaymentRequestDirty({
          initiationMethod: selectedInitiationMethod,
          customAmountNis,
          expiryDuration,
          selectedBuyerId,
          defaultBuyerId: createdBuyer?.id
        })}
        activeTab="payment-request"
        isBuyerCreated={true}
        nextLabel={t('view.buyers.addBuyerModal.footer.sendPaymentRequest')}
        nextDisabled={isNextDisabled}
        backLabel={t('view.buyers.addBuyerModal.footer.back')}
        nextIsLoading={createPaymentMutation.isPending}
        onNextClick={handleSendPaymentRequest}
        onBackClick={resetFlow}
      >
        <PaymentRequestTabContent
          amount={amount}
          onAmountChange={setCustomAmountNis}
          maxAmountNis={maxAmountNis}
          remainingAmountNis={remainingAmountNis}
          assetLabel={purchase ? formatPurchaseAssetNames(purchase.assets, t) : undefined}
          buyers={paymentBuyerOptions}
          selectedBuyerId={effectiveBuyerId}
          onBuyerChange={setSelectedBuyerId}
          initiationMethod={initiationMethod}
          onInitiationMethodChange={setInitiationMethod}
          transferType={transferType}
          onTransferTypeChange={setTransferType}
          isZahavOnly={isZahavOnly}
          expiryDuration={expiryDuration}
          onExpiryDurationChange={setExpiryDuration}
          saveError={createPaymentMutation.error}
        />
      </BuyersModal>

      {/* Payment Sent Confirmation */}
      {purchase && initiationMethod && (
        <PaymentSentModal
          isOpen={flowStep === 'payment-sent'}
          buyerName={selectedBuyerForPayment?.fullName ?? ''}
          assetLabel={formatPurchaseAssetNames(purchase.assets, t)}
          purchasePriceNis={purchase.purchasePriceNis}
          initiationMethod={initiationMethod}
          amount={amount}
          onClose={resetFlow}
        />
      )}
    </>
  );
};
